import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LOGOS } from "../lib/api";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";

function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col w-1/2 p-14 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #F97316 0, transparent 40%), radial-gradient(circle at 80% 70%, #ffffff 0, transparent 35%)" }} />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex-1 flex flex-col justify-center min-h-0">
          <img src={LOGOS.whiteWordmark} alt="360 Safety Systems" className="h-32 xl:h-40 w-auto block mb-8" />
          <h1 className="font-heading font-extrabold leading-[1.05] tracking-tight text-5xl xl:text-6xl">
            <span style={{ color: "#F97316" }}>Your Safety.</span><br />
            <span className="text-white">Documented.</span><br />
            <span className="text-white">Delivered.</span><br />
            <span className="text-white">Decided.</span>
          </h1>
          <p className="text-[#9CA3AF] mt-6 text-base leading-relaxed max-w-md">
            From first draft to farm.
          </p>
        </div>
      </div>
    </div>
  );
}

function MfaForm({ code, setCode, useRecovery, setUseRecovery, loading, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <input
        data-testid="login-mfa-input"
        type="text"
        inputMode={useRecovery ? "text" : "numeric"}
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder={useRecovery ? "Recovery code (xxxx-xxxx-xxxx)" : "6-digit authenticator code"}
        className="w-full rounded-lg px-4 py-3 text-white outline-none focus:ring-2 transition-all tracking-widest"
        style={{ backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" }}
      />
      <button
        data-testid="login-mfa-submit"
        type="submit"
        disabled={loading}
        className="w-full rounded-lg px-4 py-3 font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60"
        style={{ backgroundColor: "#F97316" }}
      >
        {loading ? "Verifying..." : "Verify & Enter"}
        {!loading && <ArrowRight size={18} />}
      </button>
      <button type="button" data-testid="login-mfa-toggle-recovery"
        onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
        className="w-full text-center text-xs text-[#9CA3AF] hover:text-white">
        {useRecovery ? "Use authenticator app instead" : "Lost your device? Use a recovery code"}
      </button>
    </form>
  );
}

function LoginForm({ password, setPassword, loading, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <input
        data-testid="login-password-input"
        type="password"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        className="w-full rounded-lg px-4 py-3 text-white outline-none focus:ring-2 transition-all"
        style={{ backgroundColor: "#0A1F44", border: "1px solid #1E3A8A" }}
        onFocus={(e) => (e.target.style.boxShadow = "0 0 0 2px #F97316")}
        onBlur={(e) => (e.target.style.boxShadow = "none")}
      />
      <button
        data-testid="login-submit-button"
        type="submit"
        disabled={loading}
        className="w-full rounded-lg px-4 py-3 font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60"
        style={{ backgroundColor: "#F97316" }}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#EA580C")}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#F97316")}
      >
        {loading ? "Verifying..." : "Enter Portal"}
        {!loading && <ArrowRight size={18} />}
      </button>
    </form>
  );
}

function LoginCard() {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      await login(password.trim());
      toast.success("Welcome back");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401 && detail && detail.mfa_required) {
        setMfaRequired(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Incorrect password");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      await login(password.trim(), useRecovery ? { recovery_code: code.trim() } : { code: code.trim() });
      toast.success("Welcome back");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid code — try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl p-8 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-6"
        style={{ backgroundColor: "rgba(249,115,22,0.15)" }}>
        <Lock size={22} style={{ color: "#F97316" }} />
      </div>
      <h1 className="font-heading text-white text-2xl font-bold">
        {mfaRequired ? "Two-Factor Verification" : "Portal Login"}
      </h1>
      <p className="text-[#9CA3AF] text-sm mt-1 mb-6">
        {mfaRequired
          ? "Enter the code from your authenticator app."
          : "Enter your access password to continue."}
      </p>
      {mfaRequired ? (
        <MfaForm code={code} setCode={setCode} useRecovery={useRecovery} setUseRecovery={setUseRecovery}
          loading={loading} onSubmit={submitMfa} />
      ) : (
        <LoginForm password={password} setPassword={setPassword} loading={loading} onSubmit={submit} />
      )}
    </div>
  );
}

export default function Login() {
  return (
    <div className="min-h-screen w-full flex" style={{ backgroundColor: "#0A1F44" }}>
      <BrandPanel />

      {/* Right login */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex justify-center mb-10">
            <img src={LOGOS.whiteWordmark} alt="360 Safety Systems" className="h-28 w-auto" />
          </div>
          <LoginCard />
          <p className="text-center text-[#6B7280] text-xs mt-6">
            © {new Date().getFullYear()} 360 Safety Systems
          </p>
        </div>
      </div>
    </div>
  );
}
